/**
 * The TidalVenue mark, as geometry rather than as a file.
 *
 * This module is the ONLY definition of the artwork. Two very different consumers read it:
 *
 *   - BrandMark.tsx renders it as inline JSX, so the mark in the product is vector, scales with
 *     its container, and costs no extra request;
 *   - scripts/generate-brand-assets.mjs imports this file directly (Node strips the type
 *     annotations natively, the same trick scripts/generate-asset-headers.mjs already uses) and
 *     writes web/public/brand/*.svg plus every raster size from these same constants.
 *
 * Keeping one definition is the whole point. The alternative, an SVG file for the browser and a
 * hand-copied JSX version for the application, is two drawings that agree until somebody edits
 * one of them, and the disagreement shows up as a favicon that no longer matches the product.
 *
 * The source artwork is tidalvenue-lockup.svg in the repository root. Its right-hand wordmark is
 * live <text> set in Outfit, which the product now serves from its own origin (web/public/fonts), so
 * the wordmark drawn by BrandLockup and the wordmark in the source file are the same face. Nothing
 * here depends on it: every variant below is pure geometry, and the product draws the word
 * "TidalVenue" as ordinary text, in the two tones brandWordmark describes. See docs/23.
 */

/** The three brand colors, in the order the tile gradient runs. */
export const brandColors = {
  /** Shallow water. The gradient starts here, at the top left of the tile. */
  aqua: "#31E6CB",
  /** Mid water. The gradient's middle stop, and the color of the wordmark's lighter half. */
  sky: "#12A7E6",
  /** Deep water. The gradient ends here, at the bottom right of the tile. */
  deep: "#1B3E96",
} as const;

/**
 * The artwork is drawn on a 500 by 500 grid. Every coordinate below is in that space, and every
 * variant declares `viewBox="0 0 500 500"`, so the mark is resolution independent and the numbers
 * here can be compared directly against the source file.
 */
export const canvas = 500;

/** Corner radius of the tile. Roughly 22% of the canvas, which is the iOS squircle proportion. */
export const tileRadius = 112;

/**
 * The tile gradient, in user space rather than in object bounding-box units.
 *
 * userSpaceOnUse matters: the window circle is filled with this same gradient so that it reads as
 * a hole cut through the white pin back to the tile behind it. Under objectBoundingBox units the
 * circle would get its own miniature copy of the gradient and the illusion would collapse.
 */
export const tileGradient = {
  x1: 40,
  y1: 20,
  x2: 470,
  y2: 490,
  stops: [
    { offset: 0, color: brandColors.aqua },
    { offset: 0.48, color: brandColors.sky },
    { offset: 1, color: brandColors.deep },
  ],
} as const;

/**
 * The location pin: a 140 radius arc centered near (250, 205), closed down to a point at (250, 430).
 *
 * Its extremes are what make the maskable variant work without redrawing anything. The furthest
 * point from the canvas center is the top of the arc at (250, 65), which is 185 units away, and
 * Android's maskable safe zone is a circle of radius 200. The art already fits; MASKABLE_SCALE
 * below only buys additional margin.
 */
export const pinPath = "M 140.4 292.1 A 140 140 0 1 1 359.6 292.1 L 250 430 Z";

/** The circular window cut through the pin. Both waves are clipped to it. */
export const windowCircle = { cx: 250, cy: 207, r: 101 } as const;

/** The further wave, held at 42% so it reads as water behind the near one rather than as a second line. */
export const waveBackPath =
  "M 145 208 C 176 186, 205 186, 233 206 C 258 224, 286 226, 316 208 C 335 197, 356 200, 358 200 L 358 320 L 145 320 Z";

/** The near wave, solid white. This is the shape that still reads at 16 pixels. */
export const waveFrontPath =
  "M 145 233 C 175 214, 203 214, 231 232 C 258 249, 289 250, 318 233 C 337 223, 356 227, 358 227 L 358 320 L 145 320 Z";

/** Opacity of the further wave. */
export const waveBackOpacity = 0.42;

/**
 * How far the art shrinks for the maskable variant.
 *
 * A maskable icon is cropped by whatever shape the launcher feels like using, so only the central
 * 80% circle is guaranteed visible. 0.92 puts the furthest point of the art 170 units from center
 * against a 200 unit safe radius, which survives an aggressive circular mask with room to spare.
 */
export const maskableScale = 0.92;

/** The product name. Kept here so artwork titles and the accessible name cannot drift apart. */
export const brandName = "TidalVenue";

/**
 * The name as the lockup sets it: two words in two tones, the second in the lighter water. Held
 * beside the name rather than typed where it is drawn, and web/src/brand/brand.test.ts holds the
 * two halves to brandName, so the split cannot drift from the word.
 */
export const brandWordmark = { lead: "Tidal", tail: "Venue" } as const;

function gradientMarkup(id: string): string {
  const stops = tileGradient.stops
    .map((stop) => `      <stop offset="${stop.offset}" stop-color="${stop.color}"/>`)
    .join("\n");
  return [
    `    <linearGradient id="${id}" x1="${tileGradient.x1}" y1="${tileGradient.y1}" x2="${tileGradient.x2}" y2="${tileGradient.y2}" gradientUnits="userSpaceOnUse">`,
    stops,
    "    </linearGradient>",
  ].join("\n");
}

function svgDocument(body: string, defs: string, label: string): string {
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${canvas} ${canvas}" width="${canvas}" height="${canvas}" role="img" aria-label="${label}">`,
    `  <title>${label}</title>`,
    "  <defs>",
    defs,
    "  </defs>",
    body,
    "</svg>",
    "",
  ].join("\n");
}

/**
 * The primary mark: the rounded tile.
 *
 * Used for the SVG favicon, the standard PWA icons, and the mark drawn beside the product name in
 * the application. Its corners are transparent, which is correct everywhere the platform draws an
 * icon exactly as supplied, and wrong everywhere the platform applies a mask of its own. Those
 * cases take the full-bleed variants below instead.
 */
export function iconSVG(): string {
  const body = [
    `  <rect width="${canvas}" height="${canvas}" rx="${tileRadius}" fill="url(#tile)"/>`,
    `  <path d="${pinPath}" fill="#FFFFFF"/>`,
    `  <circle cx="${windowCircle.cx}" cy="${windowCircle.cy}" r="${windowCircle.r}" fill="url(#tile)"/>`,
    '  <g clip-path="url(#window)">',
    `    <path d="${waveBackPath}" fill="#FFFFFF" opacity="${waveBackOpacity}"/>`,
    `    <path d="${waveFrontPath}" fill="#FFFFFF"/>`,
    "  </g>",
  ].join("\n");
  const defs = [
    gradientMarkup("tile"),
    `    <clipPath id="window"><circle cx="${windowCircle.cx}" cy="${windowCircle.cy}" r="${windowCircle.r}"/></clipPath>`,
  ].join("\n");
  return svgDocument(body, defs, brandName);
}

/**
 * The full-bleed variants, for the platforms that supply their own silhouette.
 *
 * Both drop the corner radius, because a rounded source cropped a second time by the platform
 * comes back visibly thinner than the platform's own shape, and both fill the canvas edge to edge
 * so there is no transparent corner to be composited onto a color nobody chose.
 */
function fullBleedSVG(scale: number, label: string): string {
  const center = canvas / 2;
  const body = [
    `  <rect width="${canvas}" height="${canvas}" fill="url(#tile)"/>`,
    `  <g transform="translate(${center} ${center}) scale(${scale}) translate(-${center} -${center})">`,
    `    <path d="${pinPath}" fill="#FFFFFF"/>`,
    `    <circle cx="${windowCircle.cx}" cy="${windowCircle.cy}" r="${windowCircle.r}" fill="url(#tile)"/>`,
    '    <g clip-path="url(#window)">',
    `      <path d="${waveBackPath}" fill="#FFFFFF" opacity="${waveBackOpacity}"/>`,
    `      <path d="${waveFrontPath}" fill="#FFFFFF"/>`,
    "    </g>",
    "  </g>",
  ].join("\n");
  const defs = [
    gradientMarkup("tile"),
    `    <clipPath id="window"><circle cx="${windowCircle.cx}" cy="${windowCircle.cy}" r="${windowCircle.r}"/></clipPath>`,
  ].join("\n");
  return svgDocument(body, defs, label);
}

/**
 * The maskable variant, for Android launchers and anything else that crops an icon to its own shape.
 *
 * Only the central 80% circle is guaranteed to survive that crop, so the art shrinks by
 * maskableScale to sit comfortably inside it.
 */
export function maskableSVG(): string {
  return fullBleedSVG(maskableScale, `${brandName} maskable icon`);
}

/**
 * The home screen icon, which iOS rounds itself.
 *
 * The art stays at full size rather than shrinking: iOS crops to a superellipse of roughly the
 * same 22% radius the tile already uses, so nothing near a corner is at risk, and shrinking would
 * only leave the mark looking smaller than every other icon on the home screen.
 */
export function appleTouchSVG(): string {
  return fullBleedSVG(1, `${brandName} home screen icon`);
}

/**
 * The single-color variant, for Safari's pinned-tab mask and the manifest's monochrome purpose.
 *
 * Both of those consumers throw the colors away and keep only the coverage, so the gradient has to
 * become a silhouette rather than merely be desaturated. The mask below fills the pin, punches the
 * window out of it, and puts the near wave back inside the hole: a teardrop with a wave in it, which
 * is the part of the mark that survives being reduced to one color at 16 pixels.
 */
export function monoSVG(): string {
  const body = [
    `  <rect width="${canvas}" height="${canvas}" fill="#000000" mask="url(#mono)"/>`,
  ].join("\n");
  const defs = [
    `    <clipPath id="window"><circle cx="${windowCircle.cx}" cy="${windowCircle.cy}" r="${windowCircle.r}"/></clipPath>`,
    `    <mask id="mono" maskUnits="userSpaceOnUse" x="0" y="0" width="${canvas}" height="${canvas}">`,
    `      <rect width="${canvas}" height="${canvas}" fill="#000000"/>`,
    `      <path d="${pinPath}" fill="#FFFFFF"/>`,
    `      <circle cx="${windowCircle.cx}" cy="${windowCircle.cy}" r="${windowCircle.r}" fill="#000000"/>`,
    '      <g clip-path="url(#window)">',
    `        <path d="${waveFrontPath}" fill="#FFFFFF"/>`,
    "      </g>",
    "    </mask>",
  ].join("\n");
  return svgDocument(body, defs, `${brandName} monochrome icon`);
}
